//@ts-strict-ignore
import React, { useContext, useState } from 'react';
import hasValidationErrors from '../../../components/connectedForm/utils/hasValidationErrors';
import { createBackgroundJob, useBackgroundProcess } from '../../../utils/BackgroundJobs';
import { getMutationProps } from '../../sales/DocumentAction';
import SalesInvoice from '../../../graphql/entities/salesLedger/SalesInvoice';
import DocumentModal2 from '../../../components/modals/DocumentModal2';
import { onDocumentComplete, onDocumentPrintFailed } from '../../../utils/DocumentUtils';
import { useMutation, useQuery } from '@apollo/client';
import SalesInvoiceExportEdi from '../../../graphql/mutations/salesLedger/SalesInvoiceExportEdi.graphql';
import EdiConfigurationListQuery from '../../../graphql/queries/sales/EdiConfigurationListQuery.graphql';
import { useComponentQueryErrorHandling } from '../../../utils/parseErrorResponse';
import WorkspaceContext from '../../../components/Context/WorkspaceContext';
import { IDeprecatedWorkspaceAction, IWorkspaceAction } from '../../../@types/workspaceTypes';
import { DocumentNode } from 'graphql';
import {
  EdiType,
  IEdiConfigurationsDropdownQueryResult,
  IEdiConfigurationsDropdownQueryVariables,
  ISalesInvoiceExportEdiMutationResult,
  ISalesInvoiceExportEdiMutationVariables,
} from '../../../graphql/schema/p4types';
import { useActionCriteria, useActionVariables } from '../../../utils/useActionProps';
import * as entities from '../../../graphql/entities';
import { IFormAction } from '../../../@types/entityTypes';

export enum SalesInvoiceWorkspaceAction {
  PrintInvoice = 'SALES_LEDGER_INVOICES_PRINT_INVOICE',
  ExportToXedi = 'EXPORT_TO_XEDI',
  ExportToEDI = 'EXPORT_TO_EDI',
}

const useExportToEditAction: ({ mutation: DocumentNode, type: EdiType }) => IWorkspaceAction = ({ mutation, type }) => {
  const workspace = useContext(WorkspaceContext);
  const handleQueryError = useComponentQueryErrorHandling();

  const { loading: ediConfigurationsLoading, data: ediConfigurations } = useQuery<
    IEdiConfigurationsDropdownQueryResult,
    IEdiConfigurationsDropdownQueryVariables
  >(EdiConfigurationListQuery, {
    fetchPolicy: 'network-only',
    onError: handleQueryError,
  });
  const [exportToXedi] = useMutation<ISalesInvoiceExportEdiMutationResult, ISalesInvoiceExportEdiMutationVariables>(
    mutation,
    {
      onError: handleQueryError,
    }
  );

  const runBackgroundProcess = useBackgroundProcess({
    title: `Exporting to ${type}`,
    onComplete: () => workspace.reloadData?.(),
  });

  const salesInvoiceExportToEdiCsvVariables: ISalesInvoiceExportEdiMutationVariables = useActionVariables(
    'salesInvoices',
    { ediType: type }
  );

  const onClick = () => {
    exportToXedi({
      variables: salesInvoiceExportToEdiCsvVariables,
    }).then((response) => {
      const responseData = response?.data;
      if (responseData) {
        const key = Object.keys(responseData)[0];
        runBackgroundProcess(responseData[key].result);
      }
    });
  };

  const disabled = ediConfigurationsLoading || ediConfigurations?.list?.count === 0;

  return {
    key: 'SalesInvoiceExportEdi',
    text: `Export to ${type}`,
    onClick: onClick,
    actionReference: SalesInvoiceWorkspaceAction.ExportToEDI,
    disabled,
  };
};

export const useSalesInvoiceWorkspaceActions = () => {
  const exportToEdiAction = useExportToEditAction({ mutation: SalesInvoiceExportEdi, type: EdiType.Edi });
  const exportToXediAction = useExportToEditAction({ mutation: SalesInvoiceExportEdi, type: EdiType.Xedi });

  const [isOpen, setIsOpen] = useState(false);

  const { selectionState } = useContext(WorkspaceContext);

  const actionCriteria = useActionCriteria();

  const modals = (
    <>
      <DocumentModal2
        modalOpen={isOpen}
        entity={entities.SalesInvoice}
        title="Print Sales Invoice"
        modalWrapper={true}
        multiValue={true}
        count={selectionState.count}
        useCustomerDefaults={true}
        blockNavigation={true}
        onClose={() => setIsOpen(false)}
        additionalInputVariables={{ actionCriteria }}
      />
    </>
  );
  const actions: Array<IFormAction> = [
    {
      key: 'printInvoices',
      text: 'Print Invoices',
      onClick: () => setIsOpen(true),
      actionReference: SalesInvoiceWorkspaceAction.PrintInvoice,
    },
    exportToEdiAction,
    exportToXediAction,
  ];

  return [actions, modals];
};

export const workspaceActionsForPermissions: Array<
  IWorkspaceAction<SalesInvoiceWorkspaceAction> | IDeprecatedWorkspaceAction<SalesInvoiceWorkspaceAction>
> = [
  { actionReference: SalesInvoiceWorkspaceAction.PrintInvoice },
  { actionReference: SalesInvoiceWorkspaceAction.ExportToEDI },
  { actionReference: SalesInvoiceWorkspaceAction.ExportToXedi },
];

function onComplete(state, props, job) {
  const { overrideOutput, outputType, showSummary } = state;
  const { dispatch, onClose, onComplete, onProcessSummary, onProcessPreview } = props;

  onDocumentComplete({
    overrideOutput,
    outputType,
    documentName: 'Sales Invoices',
    dispatch,
    onClose: () => onClose(false, state, props, job),
    onComplete,
    showSummary,
    onProcessSummary: () => onProcessSummary(false, state, props, job),
    onProcessPreview,
    job,
  });
}

export function generate(state, props, variables, returnUrl) {
  if (hasValidationErrors(props)) return;
  createBackgroundJob(
    SalesInvoice.generate as DocumentNode,
    getMutationProps(state, props, variables, 'salesInvoice'),
    props,
    returnUrl,
    'Generate Invoice',
    (job) => onComplete(state, props, job)
  );
}

export function generateRange(state, props, variables, returnUrl) {
  const combinedVariables = {
    clientMutationId: variables?.clientMutationId,
    reportParameters: variables?.reportParameters,
    ...props?.additionalInputVariables,
  };

  if (hasValidationErrors(props)) return;
  createBackgroundJob(
    SalesInvoice.generateRange as DocumentNode,
    combinedVariables,
    props,
    returnUrl,
    'Generate Sales Invoice Document',
    (job) => onComplete(state, props, job),
    (job) => onDocumentPrintFailed(state, props, job)
  );
}
